import React from 'react';
import ReactDOM from 'react-dom';
import styles from './modal.module.css';
import { useDialog } from '../../hooks/useFocusTrap';
import { useScrollLock } from '../../hooks/useScrollLock';

/**
 * Единое модальное окно проекта — тот же вид, что у форм задач, тикетов
 * и OIDC-клиентов. Клик по фону и Escape закрывают окно; фокус удерживается
 * внутри и возвращается на открывшую кнопку после закрытия.
 *
 * Для содержимого доступны классы из modal.module.css:
 * `text` (абзац), `actions` (ряд кнопок), `btn` + `btnPrimary`/`btnSecondary`.
 */
interface ModalProps {
  title: React.ReactNode;
  onClose: () => void;
  /** Ширина панели, px (по умолчанию 560, как у формы OIDC-клиента). */
  width?: number;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ title, onClose, width, children }) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  useDialog(panelRef, onClose);
  useScrollLock();
  const titleId = React.useId();
  // Окно выносится в корень панели (там переменные темы) или в body: внутри
  // анимированного контента оно оказывалось под нижними вкладками на телефоне
  const host = (typeof document !== 'undefined'
    && (document.querySelector('.panel-theme') as HTMLElement | null)) || document.body;
  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={panelRef}
        className={styles.panel}
        style={width ? { width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <h3 id={titleId} className={styles.title}>{title}</h3>
        {children}
      </div>
    </div>,
    host,
  );
};

export default Modal;
